import { useEffect, useState } from 'react';

import { StatusBanner } from '@/components/ui/StatusBanner';
import { type SyncState } from '@/lib/sync-status';

export interface SyncStatusBannerProps {
  state: SyncState;
  /** Number of local changes not yet uploaded. */
  pending?: number;
  /** When the last sync completed; null before the first-ever sync. */
  lastSyncedAt?: Date | null;
  className?: string;
}

/**
 * Coarser than `timeAgo` (lib/format): freshness reassurance, not a log
 * timestamp, so it never falls back to a calendar date.
 */
function sinceLabel(date: Date, now: Date): string {
  const mins = Math.floor(Math.max(0, now.getTime() - date.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const FRESHNESS_TICK_MS = 30_000;

/**
 * The sync status banner (see CONTEXT.md): the single surface reporting sync
 * health. Always renders — the synced states are what native's pull-to-peek
 * reveals (web pins only the attention states). The reconnected/merging gating
 * (why routine uploads don't flash it) lives in `useSyncStatus`; this stays
 * presentational.
 */
export function SyncStatusBanner({
  state,
  pending = 0,
  lastSyncedAt = null,
  className,
}: SyncStatusBannerProps) {
  // "last synced Xm ago" drifts stale while the banner is mounted; tick a
  // re-render while that line is the one showing.
  const showsFreshness = state === 'synced' && pending === 0 && lastSyncedAt !== null;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!showsFreshness) return;
    const id = setInterval(() => setTick((t) => t + 1), FRESHNESS_TICK_MS);
    return () => clearInterval(id);
  }, [showsFreshness]);

  if (state === 'offline') {
    return (
      <StatusBanner
        variant="caution"
        icon="dot"
        title="Offline — keep checking"
        subtitle={
          pending > 0
            ? `${pending} change${pending === 1 ? '' : 's'} saved on this device · will sync when you reconnect`
            : 'Nothing is lost · changes sync when you reconnect'
        }
        className={className}
      />
    );
  }

  if (state === 'syncing' && pending > 0) {
    return (
      <StatusBanner
        variant="positive"
        showSpinner
        title="Reconnected"
        subtitle="Merging your changes…"
        className={className}
      />
    );
  }

  if (lastSyncedAt === null) {
    return (
      <StatusBanner
        variant="positive"
        showSpinner
        title="Syncing"
        subtitle="Setting things up…"
        className={className}
      />
    );
  }

  if (pending > 0) {
    return (
      <StatusBanner
        variant="positive"
        showSpinner
        title="Syncing"
        subtitle={`${pending} change${pending === 1 ? '' : 's'} uploading…`}
        className={className}
      />
    );
  }

  return (
    <StatusBanner
      variant="neutral"
      icon="check"
      title="Synced"
      subtitle={`Up to date · last synced ${sinceLabel(lastSyncedAt, new Date())}`}
      className={className}
    />
  );
}
